import './styles.css';

import BlfWorker from './blf.worker?worker';
import { buildAsc, buildCsv, downloadText, filterFrames, formatBytes, isVectorBlfHeader, MAX_FILE_SIZE, safeBaseName } from './lib';
import type { TraceFilters, TraceFrame, TraceSummary, WorkerOutbound } from './types';

const SAMPLE_URL =
  'https://raw.githubusercontent.com/hardbyte/python-can/b4f82abede25ff83376be793a2935c41f81c3869/test/data/issue_1905.blf';
const SAMPLE_NAME = 'issue_1905.blf';
const DEFAULT_TITLE = 'BLF Viewer Online — Open Vector CAN Logs Locally';
const ROW_HEIGHT = 38;
const OVERSCAN = 8;

function getElement<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Missing required element #${id}`);
  return element as T;
}

const elements = {
  fileInput: getElement<HTMLInputElement>('fileInput'),
  chooseFile: getElement<HTMLButtonElement>('chooseFile'),
  dropZone: getElement<HTMLDivElement>('dropZone'),
  loadSample: getElement<HTMLButtonElement>('loadSample'),
  ingestStatus: getElement<HTMLDivElement>('ingestStatus'),
  statusCode: document.querySelector<HTMLSpanElement>('.status-code')!,
  statusMessage: getElement<HTMLSpanElement>('statusMessage'),
  runtimeState: getElement<HTMLDivElement>('runtimeState'),
  runtimeLabel: getElement<HTMLElement>('runtimeLabel'),
  emptyWorkspace: getElement<HTMLDivElement>('emptyWorkspace'),
  errorPanel: getElement<HTMLDivElement>('errorPanel'),
  errorCode: getElement<HTMLSpanElement>('errorCode'),
  errorTitle: getElement<HTMLElement>('errorTitle'),
  errorMessage: getElement<HTMLParagraphElement>('errorMessage'),
  dismissError: getElement<HTMLButtonElement>('dismissError'),
  results: getElement<HTMLDivElement>('results'),
  fileName: getElement<HTMLElement>('fileName'),
  fileMeta: getElement<HTMLElement>('fileMeta'),
  frameCount: getElement<HTMLElement>('frameCount'),
  frameTypeMix: getElement<HTMLElement>('frameTypeMix'),
  channelCount: getElement<HTMLElement>('channelCount'),
  channelList: getElement<HTMLElement>('channelList'),
  idCount: getElement<HTMLElement>('idCount'),
  duration: getElement<HTMLElement>('duration'),
  warningPanel: getElement<HTMLElement>('warningPanel'),
  warningMessage: getElement<HTMLElement>('warningMessage'),
  searchInput: getElement<HTMLInputElement>('searchInput'),
  channelFilter: getElement<HTMLSelectElement>('channelFilter'),
  typeFilter: getElement<HTMLSelectElement>('typeFilter'),
  directionFilter: getElement<HTMLSelectElement>('directionFilter'),
  exportCsv: getElement<HTMLButtonElement>('exportCsv'),
  exportAsc: getElement<HTMLButtonElement>('exportAsc'),
  resetTrace: getElement<HTMLButtonElement>('resetTrace'),
  visibleCount: getElement<HTMLElement>('visibleCount'),
  tableViewport: getElement<HTMLDivElement>('tableViewport'),
  tableSpacer: getElement<HTMLDivElement>('tableSpacer'),
  tableRows: getElement<HTMLDivElement>('tableRows'),
  noMatches: getElement<HTMLElement>('noMatches'),
};

const state: {
  fileName: string;
  fileSize: number;
  frames: TraceFrame[];
  filtered: TraceFrame[];
  summary: TraceSummary | null;
  busy: boolean;
  filterFrame: number | null;
} = {
  fileName: '',
  fileSize: 0,
  frames: [],
  filtered: [],
  summary: null,
  busy: false,
  filterFrame: null,
};

const worker = new BlfWorker();

function setStatus(code: string, message: string, tone: 'normal' | 'warning' | 'error' = 'normal'): void {
  elements.statusCode.textContent = code;
  elements.statusMessage.textContent = message;
  elements.ingestStatus.dataset.tone = tone;
}

function setBusy(busy: boolean): void {
  state.busy = busy;
  elements.dropZone.classList.toggle('is-busy', busy);
  elements.chooseFile.disabled = busy;
  elements.loadSample.disabled = busy;
}

function setRuntime(label: string, active: boolean): void {
  elements.runtimeLabel.textContent = label;
  elements.runtimeState.classList.toggle('is-active', active);
}

function hideError(): void {
  elements.errorPanel.hidden = true;
}

function showError(code: string, message: string): void {
  setBusy(false);
  const runtimeUnavailable = code === 'RUNTIME_UNAVAILABLE' || code === 'WORKER_FAILED';
  setRuntime(runtimeUnavailable ? 'Unavailable' : 'Ready', !runtimeUnavailable);
  elements.results.hidden = true;
  elements.emptyWorkspace.hidden = true;
  elements.errorPanel.hidden = false;
  elements.errorCode.textContent = code.replaceAll('_', ' ').slice(0, 18);
  elements.errorTitle.textContent = errorTitle(code);
  elements.errorMessage.textContent = message;
  setStatus('STOP', message, 'error');
  elements.errorPanel.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

function errorTitle(code: string): string {
  const titles: Record<string, string> = {
    NOT_VECTOR_BLF: 'Not a Vector automotive BLF',
    TRUNCATED_OR_SIZE_MISMATCH: 'Trace looks incomplete',
    UNSUPPORTED_OBJECT_HEADER: 'Unsupported BLF object header',
    FILE_TOO_LARGE: 'File exceeds the v1 safety limit',
    RUNTIME_UNAVAILABLE: 'Local parser runtime unavailable',
  };
  return titles[code] ?? 'Unable to produce a safe result';
}

function resetTrace(showEmpty = true): void {
  state.fileName = '';
  state.fileSize = 0;
  state.frames = [];
  state.filtered = [];
  state.summary = null;
  elements.fileInput.value = '';
  elements.searchInput.value = '';
  elements.channelFilter.innerHTML = '<option value="all">All channels</option>';
  elements.typeFilter.value = 'all';
  elements.directionFilter.value = 'all';
  elements.results.hidden = true;
  elements.errorPanel.hidden = true;
  elements.emptyWorkspace.hidden = !showEmpty;
  elements.tableRows.replaceChildren();
  elements.tableSpacer.style.height = '0px';
  elements.tableViewport.scrollTop = 0;
  setBusy(false);
  if (showEmpty) setStatus('READY', 'Waiting for a Vector LOGG file.');
}

async function readFile(file: File): Promise<void> {
  hideError();
  if (file.size > MAX_FILE_SIZE) {
    showError(
      'FILE_TOO_LARGE',
      `This v1 build accepts files up to ${formatBytes(MAX_FILE_SIZE)}. The selected file is ${formatBytes(file.size)}.`,
    );
    return;
  }

  setBusy(true);
  setStatus('CHECK', `Validating ${file.name}…`);
  const bytes = await file.arrayBuffer();
  if (!isVectorBlfHeader(new Uint8Array(bytes, 0, Math.min(4, bytes.byteLength)))) {
    showError(
      'NOT_VECTOR_BLF',
      'The selected file does not start with the Vector LOGG signature. Windows CLFS and other .blf formats are not accepted.',
    );
    return;
  }

  state.fileName = file.name;
  state.fileSize = file.size;
  state.frames = [];
  state.filtered = [];
  state.summary = null;
  elements.frameCount.textContent = '0';
  elements.emptyWorkspace.hidden = true;
  elements.errorPanel.hidden = true;
  elements.results.hidden = false;
  elements.fileName.textContent = file.name;
  elements.fileMeta.textContent = `${formatBytes(file.size)} · LOGG signature accepted`;
  setRuntime('Working', true);
  worker.postMessage({ type: 'parse', fileName: file.name, bytes }, [bytes]);
}

async function loadPublicSample(): Promise<void> {
  setBusy(true);
  setStatus('FETCH', 'Fetching the pinned public CAN FD fixture…');
  try {
    const response = await fetch(SAMPLE_URL);
    if (!response.ok) throw new Error(`sample request returned ${response.status}`);
    const blob = await response.blob();
    await readFile(new File([blob], SAMPLE_NAME, { type: 'application/octet-stream' }));
  } catch (error) {
    showError('SAMPLE_UNAVAILABLE', `The public sample could not be loaded: ${String(error)}`);
  }
}

function currentFilters(): TraceFilters {
  return {
    query: elements.searchInput.value,
    channel: elements.channelFilter.value,
    frameType: elements.typeFilter.value,
    direction: elements.directionFilter.value,
  };
}

function scheduleFilter(): void {
  if (state.filterFrame !== null) cancelAnimationFrame(state.filterFrame);
  state.filterFrame = requestAnimationFrame(() => {
    state.filterFrame = null;
    state.filtered = filterFrames(state.frames, currentFilters());
    elements.visibleCount.textContent = state.filtered.length.toLocaleString();
    elements.exportCsv.disabled = state.filtered.length === 0;
    elements.exportAsc.disabled = state.filtered.length === 0;
    elements.noMatches.hidden = state.filtered.length !== 0;
    elements.tableViewport.scrollTop = 0;
    elements.tableSpacer.style.height = `${state.filtered.length * ROW_HEIGHT}px`;
    renderRows();
  });
}

function renderRows(): void {
  const viewportHeight = elements.tableViewport.clientHeight;
  const start = Math.max(0, Math.floor(elements.tableViewport.scrollTop / ROW_HEIGHT) - OVERSCAN);
  const visibleRows = Math.ceil(viewportHeight / ROW_HEIGHT) + OVERSCAN * 2;
  const end = Math.min(state.filtered.length, start + visibleRows);
  const fragment = document.createDocumentFragment();

  for (let index = start; index < end; index += 1) {
    const frame = state.filtered[index];
    const row = document.createElement('div');
    row.className = 'trace-row trace-grid';
    row.setAttribute('role', 'row');
    row.dataset.frameIndex = String(frame.index);
    const values: Array<[string, string]> = [
      ['row-number', String(frame.index)],
      ['', frame.timestamp_s.toFixed(6)],
      ['', String(frame.channel)],
      [`row-direction ${frame.direction === 'Tx' ? 'tx' : ''}`, frame.direction],
      ['row-type', frame.frame_type.replace('_', ' ')],
      ['row-id', `${frame.is_extended_id ? '0x' : ''}${frame.frame_id_hex}`],
      ['', `${frame.dlc_code.toString(16).toUpperCase()} / ${frame.data_length}`],
      ['row-data', frame.is_remote_frame ? 'REMOTE FRAME' : frame.data_hex || '—'],
    ];
    for (const [className, text] of values) {
      const cell = document.createElement('span');
      cell.className = className;
      cell.textContent = text;
      cell.title = text;
      cell.setAttribute('role', 'cell');
      row.append(cell);
    }
    fragment.append(row);
  }
  elements.tableRows.style.transform = `translateY(${start * ROW_HEIGHT}px)`;
  elements.tableRows.replaceChildren(fragment);
}

function renderSummary(summary: TraceSummary): void {
  const typeMix = Object.entries(summary.frame_types)
    .map(([type, count]) => `${type.replace('_', ' ')} ${count.toLocaleString()}`)
    .join(' · ');
  elements.frameCount.textContent = summary.frame_count.toLocaleString();
  elements.frameTypeMix.textContent = typeMix || 'No supported raw frames';
  elements.channelCount.textContent = summary.channels.length.toLocaleString();
  elements.channelList.textContent = summary.channels.length ? `CH ${summary.channels.join(' · CH ')}` : '—';
  elements.idCount.textContent = summary.unique_id_count.toLocaleString();
  elements.duration.textContent = `${summary.duration_s.toFixed(3)} s duration`;
  elements.fileMeta.textContent = [
    formatBytes(state.fileSize),
    summary.measurement_start ? `started ${summary.measurement_start}` : null,
    `${summary.declared_object_count.toLocaleString()} declared objects`,
  ]
    .filter(Boolean)
    .join(' · ');

  elements.channelFilter.innerHTML = '<option value="all">All channels</option>';
  for (const channel of summary.channels) {
    const option = document.createElement('option');
    option.value = String(channel);
    option.textContent = `Channel ${channel}`;
    elements.channelFilter.append(option);
  }

  if (summary.unsupported_object_count > 0) {
    const types = Object.entries(summary.unsupported_object_types)
      .map(([type, count]) => `${type} × ${count}`)
      .join(', ');
    elements.warningMessage.textContent =
      `${summary.unsupported_object_count.toLocaleString()} unsupported object(s) were excluded: ${types}. ` +
      'Exports are complete only for the supported raw CAN/CAN-FD rows shown below.';
    elements.warningPanel.hidden = false;
  } else {
    elements.warningPanel.hidden = true;
  }

  elements.results.hidden = false;
  elements.emptyWorkspace.hidden = true;
  elements.errorPanel.hidden = true;
  scheduleFilter();
}

worker.onmessage = (event: MessageEvent<WorkerOutbound>) => {
  const message = event.data;
  if (message.type === 'status') {
    setRuntime(message.stage === 'PARSING' ? 'Working' : 'Loading', true);
    setStatus(message.stage, message.message);
    return;
  }
  if (message.type === 'ready') {
    setRuntime('Ready', true);
    if (!state.busy) setStatus('READY', 'Local parser ready. Select a Vector LOGG file.');
    return;
  }
  if (message.type === 'chunk') {
    state.frames.push(...message.rows);
    elements.frameCount.textContent = message.parsedCount.toLocaleString();
    setStatus('PARSE', `${message.parsedCount.toLocaleString()} supported frames decoded locally…`);
    return;
  }
  if (message.type === 'complete') {
    state.summary = message.summary;
    renderSummary(message.summary);
    setBusy(false);
    setRuntime('Ready', true);
    const suffix = message.summary.unsupported_object_count
      ? `; ${message.summary.unsupported_object_count} unsupported object(s) disclosed`
      : '';
    setStatus('DONE', `${message.summary.frame_count.toLocaleString()} supported frames ready${suffix}.`, message.summary.unsupported_object_count ? 'warning' : 'normal');
    document.title = `${state.fileName} — BLF Viewer`;
    elements.results.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return;
  }
  showError(message.code, message.message);
};

worker.onerror = (event) => {
  showError('WORKER_FAILED', event.message || 'The local parser worker stopped unexpectedly.');
};

function exportVisible(kind: 'csv' | 'asc'): void {
  if (!state.filtered.length) return;
  const baseName = safeBaseName(state.fileName);
  if (kind === 'csv') {
    downloadText(`${baseName}.csv`, buildCsv(state.filtered), 'text/csv;charset=utf-8');
  } else {
    downloadText(`${baseName}.asc`, buildAsc(state.filtered, state.summary?.measurement_start), 'text/plain;charset=us-ascii');
  }
  setStatus('EXPORT', `${state.filtered.length.toLocaleString()} visible frames exported as ${kind.toUpperCase()}.`);
}

elements.chooseFile.addEventListener('click', (event) => {
  event.stopPropagation();
  elements.fileInput.click();
});
elements.dropZone.addEventListener('click', () => {
  if (!state.busy) elements.fileInput.click();
});
elements.dropZone.addEventListener('keydown', (event) => {
  if ((event.key === 'Enter' || event.key === ' ') && !state.busy) {
    event.preventDefault();
    elements.fileInput.click();
  }
});
elements.fileInput.addEventListener('change', () => {
  const file = elements.fileInput.files?.[0];
  if (file) void readFile(file);
});

for (const eventName of ['dragenter', 'dragover'] as const) {
  elements.dropZone.addEventListener(eventName, (event) => {
    event.preventDefault();
    if (!state.busy) elements.dropZone.classList.add('is-dragging');
  });
}
for (const eventName of ['dragleave', 'drop'] as const) {
  elements.dropZone.addEventListener(eventName, (event) => {
    event.preventDefault();
    elements.dropZone.classList.remove('is-dragging');
  });
}
elements.dropZone.addEventListener('drop', (event) => {
  const file = event.dataTransfer?.files[0];
  if (file && !state.busy) void readFile(file);
});

elements.loadSample.addEventListener('click', () => void loadPublicSample());
elements.dismissError.addEventListener('click', () => {
  resetTrace();
  elements.fileInput.click();
});
elements.resetTrace.addEventListener('click', () => {
  resetTrace();
  document.title = DEFAULT_TITLE;
  elements.dropZone.scrollIntoView({ behavior: 'smooth', block: 'center' });
});

for (const input of [elements.searchInput, elements.channelFilter, elements.typeFilter, elements.directionFilter]) {
  input.addEventListener('input', scheduleFilter);
  input.addEventListener('change', scheduleFilter);
}
elements.tableViewport.addEventListener('scroll', () => requestAnimationFrame(renderRows), { passive: true });
elements.exportCsv.addEventListener('click', () => exportVisible('csv'));
elements.exportAsc.addEventListener('click', () => exportVisible('asc'));
document.addEventListener('keydown', (event) => {
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
    event.preventDefault();
    elements.searchInput.focus();
  }
});

resetTrace();
setRuntime('Standby', false);
setStatus('READY', 'Select a Vector LOGG file; the local parser starts on demand.');
